import type { Address } from "viem"
import type { Config } from "wagmi"
import { action, computed, makeObservable, observable, runInAction } from "mobx"
import { isAddress } from "viem"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { TokenRepository } from "@/domains/tokens"
import type { TokenStore } from "@/domains/tokens/token-store"
import { LcAddress } from "@/domains/core"
import { EventEmitter } from "@/domains/library"
import { priceToTick, tickToPriceString } from "@/domains/manual-cl-core"
import { createNfpmPoolInteraction, extractCreatedPoolAddress } from "@/domains/thick-v2/interactions"

import type { TickSpacingOption } from "../constants"
import type { ManualClOrchestrator } from "../manual-cl-orchestrator"
import type { ManualClPoolRepository } from "../manual-cl-repository"
import type { ManualClPoolApiData } from "../types"
import { getSupportedTickSpacingsForChain, THICK_V2_NFP_MANAGER_ADDRESS } from "../constants"
import { calculateSqrtPriceX96, getCanonicalPoolOrder, getMarketPrice } from "../utils/price-calculation"

export type PoolOptionStatus = "existing" | "low-liquidity" | "new"

export interface PoolOption {
  tickSpacing: TickSpacingOption
  status: PoolOptionStatus
  rangePreview?: TickSpacingRangePreview
  existingPool?: {
    address: LcAddress
    tvlUsd: number
    volumeUsd: number
    feesUsd: number
  }
}

export interface TickSpacingRangePreview {
  lowerTick: number
  upperTick: number
  minPrice: string
  maxPrice: string
  widthPct: number
}

const LOW_LIQUIDITY_THRESHOLD = 1 // $1 USD

export class CreatePoolStore {
  // Token selection (TokenStore types - provides both address and chainId)
  token0: TokenStore | undefined = undefined
  token1: TokenStore | undefined = undefined

  // Selected pool tick spacing
  selectedTickSpacing: number | undefined = undefined

  // Price state
  initialPrice: string = "" // User-entered price (USD)
  marketPrice: number | undefined = undefined // Auto-calculated market price
  useMarketPrice: boolean = true // Whether to use market price or manual
  previewMinPrice: string = ""
  previewMaxPrice: string = ""

  /**
   * When true, the price shown in the UI and stored in `initialPrice` is
   * expressed as "token0 per 1 token1" rather than the canonical
   * "token1 per 1 token0". See Uniswap's invert-price toggle.
   */
  displayInverted: boolean = false

  // Pool creation state
  isCreating: boolean = false
  creationError: string | undefined = undefined

  // Queried pools for token pair
  queriedPools: Array<ManualClPoolApiData> = []
  isQueryingPools: boolean = false

  constructor(
    private readonly orchestrator: ManualClOrchestrator,
    private readonly poolRepository: ManualClPoolRepository,
    private readonly tokenRepository: TokenRepository,
    private readonly chainId: number,
    private readonly config: Config,
    private readonly eventBus: IEventBus,
    private readonly emitter: EventEmitter<EmitEvent>,
  ) {
    makeObservable(this, {
      token0: observable,
      token1: observable,
      selectedTickSpacing: observable,
      initialPrice: observable,
      marketPrice: observable,
      useMarketPrice: observable,
      previewMinPrice: observable,
      previewMaxPrice: observable,
      isCreating: observable,
      creationError: observable,
      queriedPools: observable,
      isQueryingPools: observable,
      displayInverted: observable,

      // Actions
      setToken0: action,
      setToken1: action,
      setSelectedTickSpacing: action,
      setInitialPrice: action,
      setPreviewMinPrice: action,
      setPreviewMaxPrice: action,
      setUseMarketPrice: action,
      invertPrice: action,
      queryPoolsForTokenPair: action,
      createPool: action,
      reset: action,

      // Computed properties
      hasBothTokens: computed,
      availableTickSpacings: computed,
      existingPoolsForPair: computed,
      allPoolOptions: computed,
      tickSpacingPreviews: computed,
      canonicalTokens: computed,
      sqrtPriceX96: computed,
      canCreatePool: computed,
    })
  }

  // Actions
  setToken0(token: TokenStore | undefined): void {
    this.token0 = token
    if (token && this.token1) {
      void this.queryPoolsForTokenPair()
      void this.calculateMarketPrice()
    }
  }

  setToken1(token: TokenStore | undefined): void {
    this.token1 = token
    if (this.token0 && token) {
      void this.queryPoolsForTokenPair()
      void this.calculateMarketPrice()
    }
  }

  setSelectedTickSpacing(tickSpacing: number | undefined): void {
    this.selectedTickSpacing = tickSpacing
  }

  setInitialPrice(price: string): void {
    this.initialPrice = price
    this.useMarketPrice = false
  }

  setPreviewMinPrice(price: string): void {
    this.previewMinPrice = price
  }

  setPreviewMaxPrice(price: string): void {
    this.previewMaxPrice = price
  }

  setUseMarketPrice(use: boolean): void {
    this.useMarketPrice = use
    if (use) {
      this.initialPrice = ""
    }
  }

  invertPrice(): void {
    this.displayInverted = !this.displayInverted
  }

  async queryPoolsForTokenPair(): Promise<void> {
    if (!this.token0 || !this.token1) {
      return
    }

    this.isQueryingPools = true
    try {
      const pools = await this.orchestrator.queryPoolsByTokenPair(this.token0, this.token1)
      runInAction(() => {
        this.queriedPools = pools
        this.isQueryingPools = false
      })
    } catch (error) {
      console.error("[CreatePoolStore.queryPoolsForTokenPair] Error querying pools:", error)
      runInAction(() => {
        this.queriedPools = []
        this.isQueryingPools = false
      })
    }
  }

  /**
   * Poll the API until a pool with the specified tick spacing appears
   * This is used after pool creation to wait for the API to index the new pool
   *
   * @param tickSpacing - The tick spacing to wait for
   * @param maxAttempts - Maximum number of polling attempts (default: 30, i.e., 30 seconds)
   * @param pollIntervalMs - Time between polls in milliseconds (default: 1000ms)
   */
  async pollForPoolWithTickSpacing(
    tickSpacing: number,
    maxAttempts: number = 30,
    pollIntervalMs: number = 1000,
  ): Promise<ManualClPoolApiData | undefined> {
    if (!this.token0 || !this.token1) {
      return undefined
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const pools = await this.orchestrator.queryPoolsByTokenPair(this.token0, this.token1)

        // Check if the pool with the specified tick spacing exists
        const pool = pools.find((pool) => pool.tickSpacing === tickSpacing)

        if (pool) {
          // Pool found! Update queried pools and return
          runInAction(() => {
            this.queriedPools = pools
          })
          return pool
        }

        // Pool not found yet, wait before next attempt
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
        }
      } catch (error) {
        console.error(`[CreatePoolStore.pollForPoolWithTickSpacing] Error on attempt ${attempt + 1}:`, error)
        // Continue polling even on error (might be transient)
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
        }
      }
    }

    // Max attempts reached, pool not found
    console.warn(
      `[CreatePoolStore.pollForPoolWithTickSpacing] Pool with tick spacing ${tickSpacing} not found after ${maxAttempts} attempts`,
    )
    // Still update with latest query results even if pool not found
    try {
      const pools = await this.orchestrator.queryPoolsByTokenPair(this.token0, this.token1)
      runInAction(() => {
        this.queriedPools = pools
      })
    } catch (error) {
      console.error("[CreatePoolStore.pollForPoolWithTickSpacing] Error on final query:", error)
    }

    return undefined
  }

  async calculateMarketPrice(): Promise<void> {
    if (!this.token0 || !this.token1) {
      return
    }

    try {
      const price = await getMarketPrice(this.token0, this.token1)
      runInAction(() => {
        this.marketPrice = price
        if (this.useMarketPrice && price !== undefined) {
          this.initialPrice = price.toFixed(6)
        }
      })
    } catch (error) {
      console.error("[CreatePoolStore.calculateMarketPrice] Error calculating market price:", error)
    }
  }

  async createPool(account: Address): Promise<Address | undefined> {
    if (!this.canCreatePool || !this.token0 || !this.token1 || !this.selectedTickSpacing) {
      return undefined
    }

    this.isCreating = true
    this.creationError = undefined

    try {
      const sqrtPriceX96 = this.sqrtPriceX96
      const canonical = this.canonicalTokens
      if (!sqrtPriceX96 || !canonical) {
        throw new Error("Invalid price calculation")
      }

      // Always submit in canonical pool order; the factory enforces token0 < token1
      const result = await createNfpmPoolInteraction({
        account,
        chainId: this.chainId,
        config: this.config,
        token0: {
          address: canonical.token0.address.toViemAddress(),
          symbol: canonical.token0.symbol,
        },
        token1: {
          address: canonical.token1.address.toViemAddress(),
          symbol: canonical.token1.symbol,
        },
        tickSpacing: this.selectedTickSpacing,
        sqrtPriceX96,
        nfpManagerAddress: THICK_V2_NFP_MANAGER_ADDRESS,
        emitter: this.emitter,
        eventBus: this.eventBus,
      })
      if (!result.success) {
        throw new Error(result.error ?? "Failed to create pool")
      }
      const createdPoolAddress = extractCreatedPoolAddress(result.simulatedResult)

      // Refresh pool query for current token pair to update tick-spacing status
      // The orchestrator will also refresh the main pool list via event subscription
      runInAction(() => {
        this.isCreating = false
      })

      // Poll for the newly created pool to appear in the API
      // This ensures the tick-spacing selector updates to show the pool exists
      if (this.token0 && this.token1 && this.selectedTickSpacing) {
        // Poll until the pool appears (max 30 seconds)
        const indexedPool = await this.pollForPoolWithTickSpacing(this.selectedTickSpacing)
        const indexedAddress = indexedPool?.address
        if (indexedAddress && isAddress(indexedAddress)) {
          return indexedAddress
        }
      }
      return createdPoolAddress
    } catch (error) {
      runInAction(() => {
        this.isCreating = false
        this.creationError = error instanceof Error ? error.message : "Failed to create pool"
      })
      return undefined
    }
  }

  reset(): void {
    this.token0 = undefined
    this.token1 = undefined
    this.selectedTickSpacing = undefined
    this.initialPrice = ""
    this.previewMinPrice = ""
    this.previewMaxPrice = ""
    this.marketPrice = undefined
    this.useMarketPrice = true
    this.displayInverted = false
    this.creationError = undefined
    this.queriedPools = []
  }

  // Computed properties

  get hasBothTokens(): boolean {
    return this.token0 !== undefined && this.token1 !== undefined
  }

  get availableTickSpacings(): Array<TickSpacingOption> {
    return getSupportedTickSpacingsForChain(this.chainId)
  }

  /**
   * Get existing pools for the selected token pair
   * Prioritizes queried pools (most recent), then merges with repository pools
   * This ensures we always have the latest data from the API query
   */
  get existingPoolsForPair(): Array<ManualClPoolApiData> {
    if (!this.token0 || !this.token1) {
      return []
    }

    // Start with queried pools (most recent data from API)
    const queriedPoolsMap = new Map<number, ManualClPoolApiData>()
    this.queriedPools.forEach((pool) => {
      if (pool.tickSpacing !== undefined) {
        queriedPoolsMap.set(pool.tickSpacing, pool)
      }
    })

    const selectedToken0 = this.token0
    const selectedToken1 = this.token1

    // Also check repository for any pools we might have loaded globally
    // This ensures we catch pools that were loaded via the main pool list query
    const repositoryPools = this.poolRepository.getAll()
    const repositoryMatches = repositoryPools.filter((pool) => {
      const poolToken0 = pool.token0.address
      const poolToken1 = pool.token1.address
      return (
        (poolToken0.equals(selectedToken0.address) && poolToken1.equals(selectedToken1.address)) ||
        (poolToken0.equals(selectedToken1.address) && poolToken1.equals(selectedToken0.address))
      )
    })

    // Merge repository pools into the map (queried pools take precedence)
    repositoryMatches.forEach((pool) => {
      if (!queriedPoolsMap.has(pool.tickSpacing)) {
        // Convert repository pool to API data format
        queriedPoolsMap.set(pool.tickSpacing, {
          address: pool.address.toString(),
          feeTier: pool.feeTier,
          tickSpacing: pool.tickSpacing,
          totalValueLockedUsd: pool.tvlUsd,
          volumeUsd: pool.volumeUsd,
          feesUsd: pool.feesUsd,
          token0: {
            address: pool.token0.address.toString(),
            decimals: pool.token0.decimals,
            name: pool.token0.name,
            symbol: pool.token0.symbol,
            priceUsd: pool.token0.bestPrice,
          },
          token1: {
            address: pool.token1.address.toString(),
            decimals: pool.token1.decimals,
            name: pool.token1.name,
            symbol: pool.token1.symbol,
            priceUsd: pool.token1.bestPrice,
          },
          untrackedVolumeUsd: 0,
          totalValueLockedToken0: 0,
          totalValueLockedToken1: 0,
          totalValueLockedToken0Raw: "0",
          totalValueLockedToken1Raw: "0",
          liquidityProviderCount: pool.liquidityProviderCount,
          liquidity: pool.liquidity.toString(),
          currentTick: pool.currentTick,
          currentSqrtPriceX96: pool.currentSqrtPriceX96,
          poolDayData: [],
          poolHourData: [],
        })
      }
    })

    return Array.from(queriedPoolsMap.values())
  }

  /**
   * Get all possible pool options (existing + all tick spacings)
   * Like Aerodrome - shows all possible pools, not just existing ones
   */
  get allPoolOptions(): Array<PoolOption> {
    if (!this.hasBothTokens) {
      return []
    }

    const tickSpacings = this.availableTickSpacings
    const existingPools = this.existingPoolsForPair
    const previews = new Map(this.tickSpacingPreviews.map((preview) => [preview.tickSpacing, preview.preview]))

    return tickSpacings.map((tickSpacing) => {
      const existingPool = existingPools.find((pool) => pool.tickSpacing === tickSpacing.key)
      const rangePreview = previews.get(tickSpacing.key)

      if (!existingPool) {
        // Pool doesn't exist yet - new pool option
        return {
          tickSpacing,
          status: "new" as PoolOptionStatus,
          rangePreview,
        }
      }

      // Pool exists - determine if it's low liquidity
      const tvlUsd = existingPool.totalValueLockedUsd ?? 0
      const status: PoolOptionStatus = tvlUsd < LOW_LIQUIDITY_THRESHOLD ? "low-liquidity" : "existing"

      return {
        tickSpacing,
        status,
        rangePreview,
        existingPool: {
          address: new LcAddress(existingPool.address),
          tvlUsd,
          volumeUsd: existingPool.volumeUsd ?? 0,
          feesUsd: existingPool.feesUsd ?? 0,
        },
      }
    })
  }

  get tickSpacingPreviews(): Array<{
    tickSpacing: number
    preview: TickSpacingRangePreview
  }> {
    const canonical = this.canonicalTokens
    const basePrice = this.currentPriceInput
    if (!canonical || basePrice === undefined || basePrice <= 0) return []

    const minPrice = parsePositiveNumber(this.previewMinPrice) ?? basePrice * 0.99
    const maxPrice = parsePositiveNumber(this.previewMaxPrice) ?? basePrice * 1.01
    const lowerDisplayPrice = Math.min(minPrice, maxPrice)
    const upperDisplayPrice = Math.max(minPrice, maxPrice)
    const effectiveInverted = this.displayInverted !== canonical.userSelectionInverted

    return this.availableTickSpacings.flatMap((spacing) => {
      try {
        const rawLowerTick = priceToTick({
          price: lowerDisplayPrice.toString(),
          token0: canonical.token0,
          token1: canonical.token1,
          tickSpacing: spacing.key,
          invert: effectiveInverted,
        })
        const rawUpperTick = priceToTick({
          price: upperDisplayPrice.toString(),
          token0: canonical.token0,
          token1: canonical.token1,
          tickSpacing: spacing.key,
          invert: effectiveInverted,
        })
        let lowerTick = Math.min(rawLowerTick, rawUpperTick)
        let upperTick = Math.max(rawLowerTick, rawUpperTick)
        lowerTick = Math.floor(lowerTick / spacing.key) * spacing.key
        upperTick = Math.ceil(upperTick / spacing.key) * spacing.key
        if (upperTick <= lowerTick) {
          upperTick = lowerTick + spacing.key
        }
        const minSnappedPrice = tickToPriceString({
          tick: lowerTick,
          token0: canonical.token0,
          token1: canonical.token1,
          invert: effectiveInverted,
          decimalPlaces: 6,
        })
        const maxSnappedPrice = tickToPriceString({
          tick: upperTick,
          token0: canonical.token0,
          token1: canonical.token1,
          invert: effectiveInverted,
          decimalPlaces: 6,
        })
        const snappedMin = Number(minSnappedPrice)
        const snappedMax = Number(maxSnappedPrice)
        const widthPct =
          Number.isFinite(snappedMin) && Number.isFinite(snappedMax) && basePrice > 0
            ? (Math.abs(snappedMax - snappedMin) / basePrice) * 100
            : 0

        return [{
          tickSpacing: spacing.key,
          preview: {
            lowerTick,
            upperTick,
            minPrice: minSnappedPrice,
            maxPrice: maxSnappedPrice,
            widthPct: Number.isFinite(widthPct) ? widthPct : 0,
          },
        }]
      } catch {
        return []
      }
    })
  }

  /**
   * Return the tokens in canonical (address-sorted) Uniswap pool order,
   * plus whether the user's original selection was inverted relative to it.
   * Callers should always encode sqrtPriceX96 and build the pool transaction
   * against this canonical ordering.
   */
  get canonicalTokens(): { token0: TokenStore; token1: TokenStore; userSelectionInverted: boolean } | undefined {
    if (!this.token0 || !this.token1) return undefined
    const order = getCanonicalPoolOrder(this.token0, this.token1)
    return {
      token0: order.token0,
      token1: order.token1,
      userSelectionInverted: order.inverted,
    }
  }

  /**
   * Bit-exact sqrtPriceX96 for the current price input, evaluated in canonical
   * pool order. Handles `displayInverted` so the user can type either
   * direction and still get a valid encoding.
   */
  get sqrtPriceX96(): bigint | undefined {
    const canonical = this.canonicalTokens
    if (!canonical) return undefined

    const priceStr =
      this.useMarketPrice && this.marketPrice !== undefined
        ? this.marketPrice.toFixed(18)
        : this.initialPrice && this.initialPrice !== "0"
          ? this.initialPrice
          : undefined
    if (!priceStr) return undefined

    // The stored price is "token1/token0" using the USER's selection order.
    // `displayInverted` flips that. We then convert to the canonical pair.
    // `userSelectionInverted` tells us the user's selection was opposite to canonical.
    const effectiveInverted = this.displayInverted !== canonical.userSelectionInverted

    return calculateSqrtPriceX96(priceStr, canonical.token0, canonical.token1, effectiveInverted)
  }

  private get currentPriceInput(): number | undefined {
    if (this.useMarketPrice && this.marketPrice !== undefined) return this.marketPrice
    return parsePositiveNumber(this.initialPrice)
  }

  get canCreatePool(): boolean {
    return (
      this.hasBothTokens &&
      this.selectedTickSpacing !== undefined &&
      this.sqrtPriceX96 !== undefined &&
      !this.isCreating &&
      this.creationError === undefined
    )
  }
}

function parsePositiveNumber(value: string): number | undefined {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}
